import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const FORM_URL = "https://readdy.ai/api/form/d6u402v5ehaffuo435hg";
const WELCOME_EMAIL_URL = "https://sdydqpjhqwlzaedmsncd.supabase.co/functions/v1/send-welcome-email";

const BUSINESS_TYPES_ES = [
  "Clínica médica o dental", "Despacho legal / asesoría", "Centro de estética o bienestar",
  "Empresa de servicios técnicos", "Academia o centro formativo", "Inmobiliaria",
  "Restaurante / hostelería", "Otro tipo de negocio",
];
const BUSINESS_TYPES_EN = [
  "Medical or dental clinic", "Law firm / consultancy", "Beauty or wellness center",
  "Technical services company", "Academy or training center", "Real estate agency",
  "Restaurant / hospitality", "Other type of business",
];

const BASIC_FEATURES_ES = [
  { icon: "ri-phone-line", text: "500 minutos de voz al mes" },
  { icon: "ri-calendar-check-line", text: "Integración Google Calendar" },
  { icon: "ri-global-line", text: "1 idioma configurado" },
  { icon: "ri-bar-chart-line", text: "Dashboard básico de métricas" },
  { icon: "ri-headphone-line", text: "Soporte por email 5×8" },
];
const BASIC_FEATURES_EN = [
  { icon: "ri-phone-line", text: "500 voice minutes per month" },
  { icon: "ri-calendar-check-line", text: "Google Calendar integration" },
  { icon: "ri-global-line", text: "1 configured language" },
  { icon: "ri-bar-chart-line", text: "Basic metrics dashboard" },
  { icon: "ri-headphone-line", text: "Email support 5×8" },
];

interface Props {
  onClose: () => void;
  onOpenDemo: () => void;
}

type Step = "form" | "success";

export default function RegisterModal({ onClose, onOpenDemo }: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [step, setStep] = useState<Step>("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", business: "", phone: "" });

  const businessTypes = isEn ? BUSINESS_TYPES_EN : BUSINESS_TYPES_ES;
  const basicFeatures = isEn ? BASIC_FEATURES_EN : BASIC_FEATURES_ES;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.business) {
      setError(isEn ? "Please complete all required fields." : "Por favor, completa todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("business", form.business);
      if (form.phone) body.append("phone", form.phone);

      const [formRes] = await Promise.all([
        fetch(FORM_URL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        }),
        fetch(WELCOME_EMAIL_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            business: form.business,
            phone: form.phone || null,
            source: "registro",
            lang: i18n.language,
          }),
        }).catch(() => null),
      ]);

      if (formRes.ok) {
        setStep("success");
      } else {
        setError(isEn ? "Error sending. Please try again." : "Error al enviar. Por favor, inténtalo de nuevo.");
      }
    } catch {
      setError(isEn ? "Connection error. Try again." : "Error de conexión. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg overflow-hidden">
        <div className="relative px-8 pt-8 pb-6 text-white" style={{ background: "#0C0C0E" }}>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
            <i className="ri-close-line text-white text-sm" />
          </button>
          {step === "form" ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/20 border border-amber-400/30 rounded-full mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span className="text-amber-300 text-xs font-bold uppercase tracking-widest">{isEn ? "Free access" : "Acceso gratuito"}</span>
              </div>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{isEn ? "Start free today" : "Empieza gratis hoy"}</h2>
              <p className="text-white/60 text-sm">{isEn ? "No credit card · Set up your AI in 3 minutes" : "Sin tarjeta de crédito · Configura tu IA en 3 minutos"}</p>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/20 border border-emerald-400/30 mx-auto mb-3">
                <i className="ri-check-line text-emerald-400 text-2xl" />
              </div>
              <h2 className="text-xl font-bold mb-1">{isEn ? "Registration complete!" : "¡Registro completado!"}</h2>
              <p className="text-white/60 text-sm">{isEn ? "We've sent next steps to your email" : "Te hemos enviado los próximos pasos por email"}</p>
            </div>
          )}
        </div>

        {step === "form" ? (
          <div className="px-8 py-6">
            <div className="mb-5 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">{isEn ? "What's included in the basic plan" : "Lo que incluye el plan básico"}</p>
              <ul className="space-y-2">
                {basicFeatures.map((f) => (
                  <li key={f.text} className="flex items-center gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className={`${f.icon} text-amber-500 text-sm`} /></div>
                    <span className="text-stone-700 text-sm">{f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <form id="register-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">{isEn ? "Name" : "Nombre"} <span className="text-red-400">*</span></label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} placeholder={isEn ? "Your name" : "Tu nombre"} required className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">{isEn ? "Phone" : "Teléfono"}</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder={isEn ? "+1 600 000 000" : "+34 600 000 000"} className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">Email <span className="text-red-400">*</span></label>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder={isEn ? "you@company.com" : "tu@empresa.com"} required className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">{isEn ? "Business type" : "Tipo de negocio"} <span className="text-red-400">*</span></label>
                <select name="business" value={form.business} onChange={handleChange} required className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all cursor-pointer bg-white">
                  <option value="" disabled>{isEn ? "Select your industry" : "Selecciona tu sector"}</option>
                  {businessTypes.map((bt) => <option key={bt} value={bt}>{bt}</option>)}
                </select>
              </div>
              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
                  <i className="ri-error-warning-line flex-shrink-0" /><span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={loading} className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {loading ? (<><i className="ri-loader-4-line animate-spin" /><span>{isEn ? "Registering..." : "Registrando..."}</span></>) : (<><i className="ri-user-add-line" /><span>{isEn ? "Create free account" : "Crear cuenta gratuita"}</span></>)}
              </button>
              <p className="text-center text-stone-400 text-xs">
                {isEn ? "By signing up you accept our " : "Al registrarte aceptas los "}
                <a href="/terminos" className="text-amber-600 hover:underline cursor-pointer">{isEn ? "Terms of use" : "Términos de uso"}</a>
                {isEn ? " and " : " y la "}
                <a href="/privacidad" className="text-amber-600 hover:underline cursor-pointer">{isEn ? "Privacy policy" : "Política de privacidad"}</a>
              </p>
            </form>
          </div>
        ) : (
          <div className="px-8 py-6 space-y-4">
            <div className="p-4 bg-stone-50 border border-stone-100 rounded-2xl space-y-3">
              <p className="text-xs font-bold text-stone-700 uppercase tracking-widest">{isEn ? "Next steps" : "Próximos pasos"}</p>
              {(isEn ? [
                { icon: "ri-mail-line", text: "Check your email — we sent your access details" },
                { icon: "ri-calendar-2-line", text: "Connect Google Calendar to activate automatic scheduling" },
                { icon: "ri-robot-2-line", text: "Set up Canvas Memory with your business information" },
              ] : [
                { icon: "ri-mail-line", text: "Revisa tu email — te hemos enviado los datos de acceso" },
                { icon: "ri-calendar-2-line", text: "Conecta Google Calendar para activar la agenda automática" },
                { icon: "ri-robot-2-line", text: "Configura el Canvas Memory con los datos de tu negocio" },
              ]).map((s) => (
                <div key={s.text} className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 flex-shrink-0"><i className={`${s.icon} text-amber-500 text-sm`} /></div>
                  <span className="text-stone-600 text-sm leading-relaxed">{s.text}</span>
                </div>
              ))}
            </div>
            <button onClick={() => { onClose(); onOpenDemo(); }} className="w-full py-3.5 bg-stone-900 hover:bg-stone-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-play-circle-line" />{isEn ? "Watch interactive product demo" : "Ver demo interactiva del producto"}
            </button>
            <button onClick={onClose} className="w-full py-3 text-stone-500 hover:text-stone-700 text-sm transition-colors cursor-pointer">{isEn ? "Close" : "Cerrar"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
